import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CharacterBond,
  CharacterDraft,
  CharacterVisit,
  VisitWindowUnit,
} from '../../domain/types.js';
import type { CharacterBodySnapshot } from './characterBody.js';
import {
  bondLabel,
  bondOptionsForPair,
  bondQuestionCopy,
  outgoingBonds,
  upsertBond,
} from './characterBonds.js';

function findBond(bonds: CharacterBond[], fromId: string, toId: string): CharacterBond | undefined {
  return bonds.find((b) => b.fromId === fromId && b.toId === toId);
}

function displayName(c: CharacterDraft): string {
  return `${c.basic.firstName}${c.basic.lastInitial ? ` ${c.basic.lastInitial}.` : ''}`;
}

const visitReasons = [
  'for the evening to have dinner and hang out',
  'to watch a movie',
  'to stay for a week',
];

function newVisit(): CharacterVisit {
  return {
    visitId: `visit_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    startAt: '',
    startWindow: { n: 0, unit: 'minutes' },
    endAt: '',
    endWindow: { n: 0, unit: 'minutes' },
    reason: '',
  };
}

function pairSide(body: CharacterBodySnapshot | undefined): {
  sex: 'M' | 'F'; species: string; ageRange: string;
} {
  return {
    sex: body?.sex ?? 'M',
    species: body?.species ?? 'Human',
    ageRange: body?.ageRange ?? 'Adult',
  };
}

function PeopleIcon(): JSX.Element {
  return (
    <svg className="world-bond-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="5" cy="4" r="2.5" fill="currentColor" />
      <path d="M1 14c0-2.2 1.8-4 4-4s4 1.8 4 4" fill="currentColor" />
      <circle cx="11" cy="4.5" r="2" fill="currentColor" opacity="0.85" />
      <path d="M8 14c0-1.8 1.5-3.2 3.3-3.2.5 0 1 .1 1.4.3" fill="currentColor" opacity="0.85" />
    </svg>
  );
}

interface PendingBond {
  fromId: string;
  toId: string;
  sourceName: string;
  targetName: string;
  kind: string;
  closeness: number;
  options: ReturnType<typeof bondOptionsForPair>;
}

export default function RelationshipWire(props: {
  characters: CharacterDraft[];
  bodies: Record<string, CharacterBodySnapshot>;
  bonds: CharacterBond[];
  onChange: (bonds: CharacterBond[]) => void;
  onEditCharacter: (draftCharacterId: string) => void;
  onVisitsChange?: (characterId: string, visits: CharacterVisit[], visitor?: boolean) => void;
  adjustRelationships?: boolean;
}): JSX.Element {
  const {
    characters, bodies, bonds, onChange, onEditCharacter,
    adjustRelationships = false,
  } = props;
  const canEditVisits = props.onVisitsChange !== undefined;
  const onVisitsChange = props.onVisitsChange ?? (() => undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragFrom, setDragFrom] = useState<string | null>(null);
  const [wireEnd, setWireEnd] = useState<{ x: number; y: number } | null>(null);
  const [wireStart, setWireStart] = useState<{ x: number; y: number } | null>(null);
  const [hoverTarget, setHoverTarget] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingBond | null>(null);
  const [adjustChoice, setAdjustChoice] = useState<string | null>(null);
  const [timerCharacterId, setTimerCharacterId] = useState<string | null>(null);
  const timerCharacter = timerCharacterId
    ? characters.find((c) => c.draftCharacterId === timerCharacterId) ?? null
    : null;

  function updateVisits(visits: CharacterVisit[]): void {
    if (!timerCharacter) return;
    onVisitsChange(timerCharacter.draftCharacterId, visits, true);
  }

  // Shared builder for the wire-drop dialog and the Adjust button.
  const buildPending = useCallback((fromId: string, toId: string): PendingBond | null => {
    const source = characters.find((c) => c.draftCharacterId === fromId);
    const target = characters.find((c) => c.draftCharacterId === toId);
    if (!source || !target) return null;
    const existing = findBond(bonds, fromId, toId);
    const options = bondOptionsForPair(
      pairSide(bodies[fromId]),
      pairSide(bodies[toId]),
    );
    return {
      fromId,
      toId,
      sourceName: displayName(source),
      targetName: displayName(target),
      kind: existing?.kind ?? 'stranger',
      closeness: existing?.closeness ?? 0,
      options,
    };
  }, [characters, bodies, bonds]);

  function handleAdjust(fromId: string): void {
    const outgoing = outgoingBonds(bonds, fromId);
    if (outgoing.length === 0) return;
    if (outgoing.length === 1) {
      const next = buildPending(fromId, outgoing[0]!.toId);
      if (next) setPending(next);
      return;
    }
    setAdjustChoice(fromId);
  }

  const cancelDrag = useCallback((): void => {
    setDragFrom(null);
    setWireEnd(null);
    setWireStart(null);
    setHoverTarget(null);
  }, []);

  useEffect(() => {
    if (!dragFrom) return;
    const onMove = (e: PointerEvent): void => {
      const box = containerRef.current?.getBoundingClientRect();
      if (!box) return;
      setWireEnd({ x: e.clientX - box.left, y: e.clientY - box.top });
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el?.closest('[data-bond-target]') as HTMLElement | null;
      const targetId = row?.dataset.bondTarget ?? null;
      if (targetId && targetId !== dragFrom) setHoverTarget(targetId);
      else setHoverTarget(null);
    };
    const onUp = (e: PointerEvent): void => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el?.closest('[data-bond-target]') as HTMLElement | null;
      const targetId = row?.dataset.bondTarget ?? null;
      const fromId = dragFrom;
      cancelDrag();
      if (!fromId || !targetId || targetId === fromId) return;
      const next = buildPending(fromId, targetId);
      if (next) setPending(next);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragFrom, buildPending, cancelDrag]);

  function startDrag(fromId: string, e: React.PointerEvent<HTMLButtonElement>): void {
    e.preventDefault();
    const box = containerRef.current?.getBoundingClientRect();
    const rect = e.currentTarget.getBoundingClientRect();
    if (!box) return;
    setDragFrom(fromId);
    setWireStart({
      x: rect.left + rect.width / 2 - box.left,
      y: rect.top + rect.height / 2 - box.top,
    });
    setWireEnd({
      x: e.clientX - box.left,
      y: e.clientY - box.top,
    });
  }

  function savePending(): void {
    if (!pending) return;
    const fromSex = bodies[pending.fromId]?.sex ?? 'M';
    onChange(upsertBond(
      bonds,
      pending.fromId,
      pending.toId,
      pending.kind,
      pending.closeness,
      fromSex,
    ));
    setPending(null);
  }

  const nameById = Object.fromEntries(characters.map((c) => [c.draftCharacterId, displayName(c)]));

  return (
    <div className="world-bond-wire-wrap" ref={containerRef}>
      <ul className="world-review-list world-bond-list">
        {characters.map((c) => {
          const id = c.draftCharacterId;
          const name = displayName(c);
          const outgoing = outgoingBonds(bonds, id);
          return (
            <li
              key={id}
              data-bond-target={id}
              className={hoverTarget === id ? 'world-bond-target' : undefined}
            >
              <div className="world-bond-row-main">
                <span>
                  {name}
                  {canEditVisits && (
                    <label>
                      <input
                        type="checkbox"
                        aria-label="Visitor"
                        checked={c.visitor === true}
                        onChange={(e) => onVisitsChange(id, c.visits ?? [], e.target.checked)}
                      />
                      Visitor
                    </label>
                  )}
                </span>
                <div className="world-action-row">
                  {adjustRelationships ? (
                    <button
                      type="button"
                      className="btn-ghost"
                      aria-label={`Adjust relationship for ${name}`}
                      disabled={outgoing.length === 0}
                      onClick={() => handleAdjust(id)}
                    >
                      Adjust
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-ghost"
                      aria-label={`Edit ${name}`}
                      onClick={() => onEditCharacter(id)}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-ghost world-bond-btn"
                    aria-label={`Relationship for ${name}`}
                    onPointerDown={(e) => startDrag(id, e)}
                  >
                    <PeopleIcon />
                    Relationship
                  </button>
                  {canEditVisits && c.visitor === true && (
                    <button
                      type="button"
                      className="btn-ghost"
                      aria-label={`Timer for ${name}`}
                      onClick={() => setTimerCharacterId(id)}
                    >
                      Timer
                    </button>
                  )}
                </div>
              </div>
              {outgoing.length > 0 && (
                <ul className="world-bond-outgoing">
                  {outgoing.map((b) => (
                    <li key={`${b.fromId}-${b.toId}`}>
                      {nameById[b.toId] ?? b.toId}: {bondLabel(b.kind)} · {b.closeness}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {wireStart && wireEnd && (
        <svg className="world-bond-wire-svg" aria-hidden="true">
          <line
            x1={wireStart.x}
            y1={wireStart.y}
            x2={wireEnd.x}
            y2={wireEnd.y}
            stroke="#ffd166"
            strokeWidth="2"
          />
        </svg>
      )}

      {adjustChoice && (
        <div className="world-ai-fill-dialog" role="dialog" aria-modal="true" aria-labelledby="bond-choice-title">
          <div className="world-ai-fill-dialog-card">
            <h3 id="bond-choice-title" className="world-ai-fill-title">
              Which relationship for {nameById[adjustChoice] ?? adjustChoice}?
            </h3>
            <div className="world-bond-options">
              {outgoingBonds(bonds, adjustChoice).map((b) => (
                <button
                  key={`${b.fromId}-${b.toId}`}
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    const next = buildPending(b.fromId, b.toId);
                    setAdjustChoice(null);
                    if (next) setPending(next);
                  }}
                >
                  {nameById[b.toId] ?? b.toId}
                </button>
              ))}
            </div>
            <div className="world-action-row">
              <button type="button" className="btn-ghost" onClick={() => setAdjustChoice(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {pending && (
        <div className="world-ai-fill-dialog" role="dialog" aria-modal="true" aria-labelledby="bond-dialog-title">
          <div className="world-ai-fill-dialog-card">
            <h3 id="bond-dialog-title" className="world-ai-fill-title">
              {bondQuestionCopy(pending.sourceName, pending.targetName)}
            </h3>
            <div className="world-bond-options">
              {pending.options.map((opt) => (
                <button
                  key={opt.kind}
                  type="button"
                  className={pending.kind === opt.kind ? 'btn-primary' : 'btn-ghost'}
                  onClick={() => setPending({ ...pending, kind: opt.kind })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <label className="world-bond-closeness">
              <span>Closeness</span>
              <div className="world-bond-closeness-row">
                <span className="world-muted">Distant</span>
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={pending.closeness}
                  onChange={(e) => setPending({ ...pending, closeness: Number(e.target.value) })}
                />
                <span className="world-muted">Very close</span>
              </div>
              <span className="world-muted">{pending.closeness}/10</span>
            </label>
            <div className="world-action-row">
              <button type="button" className="btn-ghost" onClick={() => setPending(null)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={savePending}>Save</button>
            </div>
          </div>
        </div>
      )}

      {timerCharacter && (
        <div className="world-ai-fill-dialog" role="dialog" aria-modal="true" aria-labelledby="timer-dialog-title">
          <div className="world-ai-fill-dialog-card">
            <h3 id="timer-dialog-title" className="world-ai-fill-title">
              Visits for {displayName(timerCharacter)}
            </h3>
            {(timerCharacter.visits ?? []).map((visit, index) => {
              const update = (patch: Partial<CharacterVisit>): void => {
                updateVisits((timerCharacter.visits ?? []).map((current) =>
                  current.visitId === visit.visitId ? { ...current, ...patch } : current,
                ));
              };
              const updateWindow = (
                field: 'startWindow' | 'endWindow',
                patch: Partial<CharacterVisit['startWindow']>,
              ): void => update({ [field]: { ...visit[field], ...patch } });
              return (
                <fieldset key={visit.visitId} className="world-visit-card">
                  <legend>Visit {index + 1}</legend>
                  <label>
                    Start date and time
                    <input
                      type="datetime-local"
                      value={visit.startAt}
                      onChange={(e) => update({ startAt: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>within {visit.startWindow.n} {visit.startWindow.unit} of start date</span>
                    <input
                      type="number"
                      min={0}
                      max={168}
                      step={1}
                      value={visit.startWindow.n}
                      onChange={(e) => updateWindow('startWindow', { n: Math.max(0, Math.min(168, Number(e.target.value))) })}
                    />
                    <select
                      aria-label="Start window units"
                      value={visit.startWindow.unit}
                      onChange={(e) => updateWindow('startWindow', { unit: e.target.value as VisitWindowUnit })}
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </label>
                  <label>
                    End date and time
                    <input
                      type="datetime-local"
                      min={visit.startAt || undefined}
                      value={visit.endAt}
                      onChange={(e) => update({ endAt: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>within {visit.endWindow.n} {visit.endWindow.unit} of end date</span>
                    <input
                      type="number"
                      min={0}
                      max={168}
                      step={1}
                      value={visit.endWindow.n}
                      onChange={(e) => updateWindow('endWindow', { n: Math.max(0, Math.min(168, Number(e.target.value))) })}
                    />
                    <select
                      aria-label="End window units"
                      value={visit.endWindow.unit}
                      onChange={(e) => updateWindow('endWindow', { unit: e.target.value as VisitWindowUnit })}
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </label>
                  <label>
                    Reason
                    <textarea
                      maxLength={500}
                      value={visit.reason}
                      onChange={(e) => update({ reason: e.target.value })}
                    />
                  </label>
                  <div className="world-action-row">
                    {visitReasons.map((reason) => (
                      <button
                        key={reason}
                        type="button"
                        className="btn-ghost"
                        onClick={() => update({ reason })}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => updateVisits((timerCharacter.visits ?? []).filter((current) => current.visitId !== visit.visitId))}
                  >
                    Remove visit
                  </button>
                </fieldset>
              );
            })}
            <div className="world-action-row">
              <button type="button" className="btn-ghost" onClick={() => updateVisits([...(timerCharacter.visits ?? []), newVisit()])}>
                Add visit
              </button>
              <button type="button" className="btn-ghost" onClick={() => setTimerCharacterId(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
